<script>
import { mapGetters } from 'vuex';
import MentionBox from '../mentions/MentionBox.vue';

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
    conversationId: {
      type: Number,
      default: null,
    },
  },
  emits: ['replace', 'sendCannedAudio'],
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
      cannedAudios: 'getCannedAudioResponses',
      allConversations: 'getAllConversations',
    }),
    currentConversation() {
      if (!this.conversationId) return null;
      return this.allConversations.find(
        c => c.id === this.conversationId
      );
    },
    currentDealStageId() {
      return this.currentConversation?.crm_deal?.stage_id || null;
    },
    filteredCannedMessages() {
      return this.cannedMessages.filter(m => {
        // If no pipeline stage is set, always show
        if (!m.crm_pipeline_stage_id) return true;
        // If stage is set, only show if the conversation's deal matches
        return m.crm_pipeline_stage_id === this.currentDealStageId;
      });
    },
    stageItems() {
      // Group stage-specific responses by pipeline_stage_name
      const stageMap = {};
      this.filteredCannedMessages
        .filter(m => m.crm_pipeline_stage_id)
        .forEach(m => {
          const key = m.pipeline_stage_name || 'Pipeline';
          if (!stageMap[key]) stageMap[key] = [];
          stageMap[key].push({
            label: m.short_code,
            key: m.short_code,
            description: m.content,
            type: 'text',
          });
        });
      return stageMap;
    },
    globalItems() {
      return this.filteredCannedMessages
        .filter(m => m.scope === 'global' && !m.crm_pipeline_stage_id)
        .map(m => ({
          label: m.short_code,
          key: m.short_code,
          description: m.content,
          type: 'text',
        }));
    },
    personalItems() {
      return this.filteredCannedMessages
        .filter(m => m.scope === 'personal' && !m.crm_pipeline_stage_id)
        .map(m => ({
          label: m.short_code,
          key: m.short_code,
          description: m.content,
          type: 'text',
        }));
    },
    audioItems() {
      return this.cannedAudios.map(a => ({
        label: a.short_code,
        key: `audio_${a.id}`,
        description: a.subject,
        type: 'audio',
        audioUrl: a.audio_url,
        audioId: a.id,
      }));
    },
    groups() {
      const g = [];
      // Stage-specific groups first (priority)
      Object.entries(this.stageItems).forEach(([stageName, items]) => {
        g.push({ label: `📍 ${stageName}`, items });
      });
      if (this.globalItems.length) {
        g.push({ label: '🌐 Globais', items: this.globalItems });
      }
      if (this.personalItems.length) {
        g.push({ label: '👤 Minhas', items: this.personalItems });
      }
      if (this.audioItems.length) {
        g.push({ label: '🔊 Áudios', items: this.audioItems });
      }
      return g;
    },
    flatItems() {
      return this.groups.reduce((acc, group) => acc.concat(group.items), []);
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
    },
  },
  mounted() {
    this.fetchCannedResponses();
    this.fetchCannedAudioResponses();
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    fetchCannedAudioResponses() {
      this.$store.dispatch('getCannedAudioResponses', {
        searchKey: this.searchKey,
      });
    },
    handleMentionClick(item = {}) {
      if (item.type === 'audio') {
        this.$emit('sendCannedAudio', item);
      } else {
        this.$emit('replace', item.description);
      }
    },
  },
};
</script>

<template>
  <MentionBox v-if="flatItems.length" :items="flatItems" :groups="groups" @mention-select="handleMentionClick" />
</template>
